import type { WomenTripCardDTO } from "@majourneys/shared";

/**
 * The hub's filter chips, held in the URL (`?when=weekend`, `?from=<city>`)
 * so Back, refresh and a shared link keep them.
 */
export interface WomenHubSearch {
  when?: "weekend";
  from?: string;
}

export function parseWomenHubSearch(search: Record<string, unknown>): WomenHubSearch {
  const out: WomenHubSearch = {};
  if (search.when === "weekend") out.when = "weekend";
  if (typeof search.from === "string" && /^[a-z0-9-]{1,80}$/.test(search.from)) out.from = search.from;
  return out;
}

/** Weekday of a `YYYY-MM-DD`, read as a calendar day (0 = Sunday). */
function weekday(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1)).getUTCDay();
}

/** A weekend trip leaves on a Friday, Saturday or Sunday. */
export function isWeekendTrip(trip: Pick<WomenTripCardDTO, "dates">): boolean {
  return trip.dates.some((d) => [5, 6, 0].includes(weekday(d.date)));
}

/** The departure cities on the hub, in the order the trips come. */
export function departureCities(trips: readonly WomenTripCardDTO[]): { slug: string; name: string }[] {
  const seen = new Map<string, string>();
  for (const t of trips) if (t.citySlug && !seen.has(t.citySlug)) seen.set(t.citySlug, t.cityName);
  return [...seen].map(([slug, name]) => ({ slug, name }));
}

export function filterTrips(trips: readonly WomenTripCardDTO[], search: WomenHubSearch): WomenTripCardDTO[] {
  return trips.filter(
    (t) => (!search.when || isWeekendTrip(t)) && (!search.from || t.citySlug === search.from),
  );
}

/** Seats left on a card's soonest open date, or null when there is no limit. */
export function cardSeatsLeft(trip: Pick<WomenTripCardDTO, "dates">): number | null {
  const open = trip.dates.find((d) => d.seatsLeft !== 0);
  if (!open) return trip.dates.length > 0 ? 0 : null;
  return open.seatsLeft;
}

/** The date a card leads with: the soonest one that still has a seat, else the soonest. */
export function cardLeadDate(trip: Pick<WomenTripCardDTO, "dates">): string | null {
  return (trip.dates.find((d) => d.seatsLeft !== 0) ?? trip.dates[0])?.date ?? null;
}

/** "13 Nov" in the page's language, Latin digits in every locale. */
export function shortTripDate(iso: string, lang: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Intl.DateTimeFormat(`${lang}-u-nu-latn`, { day: "numeric", month: "short", timeZone: "UTC" }).format(
    new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1)),
  );
}

/**
 * Every city a trip leaves from, as one line ("Casablanca, Rabat, Mohammedia"),
 * or the trip's own city when it lists none. Arabic takes its own comma.
 */
export function departureLine(trip: Pick<WomenTripCardDTO, "cityName"> & { departures?: readonly string[] | null }, lang: string): string {
  const cities: string[] = [];
  for (const raw of trip.departures ?? []) {
    const city = typeof raw === "string" ? raw.trim() : "";
    if (city && !cities.includes(city)) cities.push(city);
  }
  return cities.length > 0 ? cities.join(lang === "ar" ? "، " : ", ") : trip.cityName;
}

/** The soonest trip date across every trip, and the trip it belongs to. Ties keep the first trip. */
export function nextDeparture<T extends Pick<WomenTripCardDTO, "dates">>(
  trips: readonly T[],
): { trip: T; date: string; seatsLeft: number | null } | null {
  let best: { trip: T; date: string; seatsLeft: number | null } | null = null;
  for (const trip of trips) {
    for (const d of trip.dates) {
      if (!best || d.date < best.date) best = { trip, date: d.date, seatsLeft: d.seatsLeft };
    }
  }
  return best;
}
